import { useState } from 'react';
import type { CatalogDepartment, CatalogSection } from '../../api/catalogApi';
import SectionHero from './SectionHero';
import ToolCard from './ToolCard';

interface SectionViewProps {
  section: CatalogSection;
  /** Present only for users who can see every tool: the chips then are departments, not tags. */
  departments?: CatalogDepartment[];
  onOpenTool: (toolId: string) => void;
}

export default function SectionView({ section, departments = [], onOpenTool }: SectionViewProps) {
  const [tag, setTag] = useState('All');

  // Department chips (only departments with at least one tool in this section) replace the tag chips.
  const sectionToolIds = new Set(section.items.map((t) => t.id));
  const deptChips = departments.filter((d) => d.tool_ids.some((id) => sectionToolIds.has(id)));
  const useDepts = deptChips.length > 0;

  const labels = useDepts
    ? ['All', ...deptChips.map((d) => d.name)]
    : ['All', ...Array.from(new Set(section.items.map((t) => t.tag)))];
  const activeTag = labels.includes(tag) ? tag : 'All';
  const activeDept = useDepts ? deptChips.find((d) => d.name === activeTag) : undefined;

  const matches = (tool: CatalogSection['items'][number]) => {
    if (activeTag === 'All') return true;
    return useDepts ? !!activeDept?.tool_ids.includes(tool.id) : tool.tag === activeTag;
  };

  return (
    <>
      <SectionHero section={section} />
      {section.id !== 'demo' && (useDepts || section.items.length > 6) && (
        <div className="filters" role="group" aria-label="Filter">
          {(useDepts ? labels : labels.slice(0, 7)).map((t) => (
            <button
              key={t}
              className="chip"
              aria-pressed={t === activeTag}
              onClick={() => setTag(t)}
            >
              {t}
            </button>
          ))}
        </div>
      )}
      <div className="tool-grid">
        {section.items.map(
          (tool, i) =>
            matches(tool) && (
              <ToolCard
                key={tool.id}
                sectionId={section.id}
                index={i}
                tool={tool}
                onOpen={() => onOpenTool(tool.id)}
              />
            ),
        )}
      </div>
    </>
  );
}
